import React, { useState } from "react";
import styled from "styled-components";

import btnSMS from "../../assets/MentalPlus/btnSMS.svg";
import btnSuppr from "../../assets/MentalPlus/BtnSuppr.png";
import btnTel from "../../assets/MentalPlus/btnTel.svg";
import iconModify from "../../assets/MentalPlus/Modifier.svg";

import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useTranslation } from "react-i18next";

import {
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCheckbox,
  IonToggle,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";

/* -------------------------------------------------------------------------- */
/*                                 TYPES                                      */
/* -------------------------------------------------------------------------- */

type EmergencyData = {
  pinned: string;
  urgences: string[];
  proches: string[];
  utiles: string[];
};

type EmergencyCategory = "urgences" | "proches" | "utiles";
type MainTab = "urgence" | "signes" | "activites" | "jecris";

/* -------------------------------------------------------------------------- */
/*                          NUMÉROS DE SOUTIENS CARDS                         */
/* -------------------------------------------------------------------------- */

function EmergencyCards() {
  const { t } = useTranslation();

  const defaultData: EmergencyData = {
    pinned: "Prévention suicide – 3114",
    urgences: [
      "Prévention suicide – 3114",
      "Urgence vitale – 112",
      "Croix-Rouge écoute – 0 800 858 858",
      "SOS Amitié – 09 72 39 40 50",
      "SOS crise – 0800 19 00 00",
    ],
    proches: [],
    utiles: [
      "Fil Santé Jeunes – 0 800 235 236",
      "Planning familial – 0 800 08 11 11",
      "Net Écoute – 3018",
    ],
  };

  const [emergencyData, setEmergencyData] = useState<EmergencyData>(() => {
    const stored = localStorage.getItem("emergencyDataV2");
    if (!stored) return defaultData;

    try {
      const parsed = JSON.parse(stored);
      return {
        pinned: parsed.pinned ?? defaultData.pinned,
        urgences: Array.isArray(parsed.urgences)
          ? parsed.urgences
          : defaultData.urgences,
        proches: Array.isArray(parsed.proches)
          ? parsed.proches
          : defaultData.proches,
        utiles: Array.isArray(parsed.utiles)
          ? parsed.utiles
          : defaultData.utiles,
      };
    } catch {
      return defaultData;
    }
  });

  const updateStorage = (newData: EmergencyData) => {
    setEmergencyData(newData);
    localStorage.setItem("emergencyDataV2", JSON.stringify(newData));
  };

  const splitLine = (line: string) => {
    const [title, number] = line.split("–");
    return {
      title: title?.trim() ?? "",
      number: number?.trim() ?? "",
    };
  };

  const sortAlphabetically = (list: string[]) => {
    return [...list].sort((a, b) => {
      const titleA = a.split("–")[0].trim().toLowerCase();
      const titleB = b.split("–")[0].trim().toLowerCase();
      return titleA.localeCompare(titleB, "fr");
    });
  };

  /* ------------------------------- PINNED --------------------------------- */

  const [isPinnedPickerOpen, setIsPinnedPickerOpen] = useState(false);
  const [pinnedSource, setPinnedSource] = useState<
    "urgences" | "proches" | "utiles" | null
  >(null);
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);

  const openPinnedPicker = () => {
    const current = emergencyData.pinned;
    let foundSource: "urgences" | "proches" | "utiles" | null = null;
    let foundIndex: number | null = null;

    const allUrgences = emergencyData.urgences || [];
    const allProches = emergencyData.proches || [];
    const allUtiles = emergencyData.utiles || [];

    const idxUrg = allUrgences.indexOf(current);
    if (idxUrg !== -1) {
      foundSource = "urgences";
      foundIndex = idxUrg;
    } else {
      const idxPro = allProches.indexOf(current);
      if (idxPro !== -1) {
        foundSource = "proches";
        foundIndex = idxPro;
      } else {
        const idxUtil = allUtiles.indexOf(current);
        if (idxUtil !== -1) {
          foundSource = "utiles";
          foundIndex = idxUtil;
        }
      }
    }

    setPinnedSource(foundSource);
    setPinnedIndex(foundIndex);
    setIsPinnedPickerOpen(true);
  };

  /* --------------------------- PROCHES : EDITION -------------------------- */

  const [isEditProchesOpen, setIsEditProchesOpen] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [isAddingProche, setIsAddingProche] = useState(false);

  const openProchesEditor = () => {
    setIsEditProchesOpen(true);
    setIsAddingProche(false);
    setManualName("");
    setManualPhone("");
  };

  const closeProchesEditor = () => {
    setIsEditProchesOpen(false);
    setIsAddingProche(false);
    setManualName("");
    setManualPhone("");
  };

  const addManualProche = () => {
    if (!manualName.trim() || !manualPhone.trim()) return;

    const line = `${manualName.trim()} – ${manualPhone.trim()}`;
    const updatedProches = [...emergencyData.proches, line];

    updateStorage({
      ...emergencyData,
      proches: updatedProches,
    });

    setManualName("");
    setManualPhone("");
    setIsAddingProche(false);
  };

  const removeProche = (lineToRemove: string) => {
    const idx = emergencyData.proches.findIndex((l) => l === lineToRemove);
    if (idx === -1) return;

    const updatedProches = [...emergencyData.proches];
    updatedProches.splice(idx, 1);

    let newPinned = emergencyData.pinned;

    if (lineToRemove === emergencyData.pinned) {
      if (updatedProches.length > 0) {
        newPinned = updatedProches[0];
      } else if (emergencyData.urgences.length > 0) {
        newPinned = emergencyData.urgences[0];
      } else if (emergencyData.utiles.length > 0) {
        newPinned = emergencyData.utiles[0];
      } else {
        newPinned = "";
      }
    }

    updateStorage({
      ...emergencyData,
      proches: updatedProches,
      pinned: newPinned,
    });
  };

  /* -------------------------- URGENCES : EDITION -------------------------- */

  const [isEditUrgencesOpen, setIsEditUrgencesOpen] = useState(false);

  const [activeUrgenceIndexes, setActiveUrgenceIndexes] = useState<number[]>(
    () => {
      const stored = localStorage.getItem("activeUrgenceIndexes");
      if (!stored) return [];
      try {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
  );

  const [selectedUrgences, setSelectedUrgences] = useState<number[]>([]);

  const openUrgencesEditor = () => {
    const sortedUrgences = sortAlphabetically(emergencyData.urgences);
    const allIndexes = sortedUrgences.map((_, idx) => idx);

    const initialSelection =
      activeUrgenceIndexes.length > 0 ? activeUrgenceIndexes : allIndexes;

    setSelectedUrgences(initialSelection);
    setIsEditUrgencesOpen(true);
  };

  const toggleUrgenceSelection = (index: number) => {
    setSelectedUrgences((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const saveUrgencesSelection = () => {
    setActiveUrgenceIndexes(selectedUrgences);
    localStorage.setItem(
      "activeUrgenceIndexes",
      JSON.stringify(selectedUrgences)
    );
    setIsEditUrgencesOpen(false);
  };

  /* --------------------------- UTILES : EDITION --------------------------- */

  const [isEditUtilesOpen, setIsEditUtilesOpen] = useState(false);

  const [activeUsefulIndexes, setActiveUsefulIndexes] = useState<number[]>(() => {
    const stored = localStorage.getItem("activeUsefulIndexes");
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [selectedUsefulIndexes, setSelectedUsefulIndexes] = useState<number[]>(
    []
  );

  const openUtilesEditor = () => {
    const sortedUtiles = sortAlphabetically(emergencyData.utiles);
    const allIndexes = sortedUtiles.map((_, idx) => idx);

    const initialSelection =
      activeUsefulIndexes.length > 0 ? activeUsefulIndexes : allIndexes;

    setSelectedUsefulIndexes(initialSelection);
    setIsEditUtilesOpen(true);
  };

  const toggleUsefulSelection = (index: number) => {
    setSelectedUsefulIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const saveUsefulSelection = () => {
    setActiveUsefulIndexes(selectedUsefulIndexes);
    localStorage.setItem(
      "activeUsefulIndexes",
      JSON.stringify(selectedUsefulIndexes)
    );
    setIsEditUtilesOpen(false);
  };

  /* ------------------------------ RENDU LISTES ---------------------------- */

  const renderListSection = (
    title: string,
    category: EmergencyCategory,
    withEditIcon = false,
    onEdit?: () => void
  ) => {
    const list = sortAlphabetically(emergencyData[category]);

    let indexesToShow: number[];

    if (category === "urgences") {
      if (activeUrgenceIndexes.length === 0) {
        indexesToShow = list.map((_, idx) => idx);
      } else {
        indexesToShow = activeUrgenceIndexes.filter(
          (i) => i >= 0 && i < list.length
        );
      }
    } else if (category === "utiles") {
      if (activeUsefulIndexes.length === 0) {
        indexesToShow = list.map((_, idx) => idx);
      } else {
        indexesToShow = activeUsefulIndexes.filter(
          (i) => i >= 0 && i < list.length
        );
      }
    } else {
      indexesToShow = list.map((_, idx) => idx);
    }

    return (
      <SectionBlock>
        <SectionHeaderRow>
          <SectionLabel>{title}</SectionLabel>

          {withEditIcon && (
            <SectionIconButton aria-label="Modifier la section" onClick={onEdit}>
              <HeaderIconImg
                src={iconModify}
                alt="Modifier"
                style={{ pointerEvents: "none" }}
              />
            </SectionIconButton>
          )}
        </SectionHeaderRow>

        <CardsColumn>
          {indexesToShow.map((index) => {
            const line = list[index];
            const { title, number } = splitLine(line);

            return (
              <SupportCard key={`${category}-${index}`}>
                <SupportInfo>
                  <SupportTitle>{title}</SupportTitle>
                  {number && <SupportNumber>{number}</SupportNumber>}
                </SupportInfo>

                <SupportActions>
                  <IconImgButton
                    aria-label="Appeler"
                    onClick={() => {
                      if (!number) return;
                      const cleanNumber = number.replace(/[^\d+]/g, "");
                      window.location.href = `tel:${cleanNumber}`;
                    }}
                  >
                    <IconImg src={btnTel} alt="Appeler" />
                  </IconImgButton>

                  <IconImgButton
                    aria-label="Envoyer un SMS"
                    onClick={() => {
                      if (!number) return;
                      const cleanNumber = number.replace(/[^\d+]/g, "");
                      window.location.href = `sms:${cleanNumber}`;
                    }}
                  >
                    <IconImg src={btnSMS} alt="SMS" />
                  </IconImgButton>
                </SupportActions>
              </SupportCard>
            );
          })}
        </CardsColumn>
      </SectionBlock>
    );
  };

  const { title: pinnedTitle, number: pinnedNumber } = splitLine(
    emergencyData.pinned
  );

  return (
    <EmergencyWrapper>
      <SectionBlock>
        <SectionHeaderRow>
          <SectionLabel>{t("mentalplus_help_num_epingle")}</SectionLabel>

          <SectionIconButton
            aria-label="Changer le numéro épinglé"
            onClick={openPinnedPicker}
          >
            <HeaderIconImg
              src={iconModify}
              alt="Changer le numéro épinglé"
              style={{ pointerEvents: "none" }}
            />
          </SectionIconButton>
        </SectionHeaderRow>

        <SupportCard>
          <SupportInfo>
            <SupportTitle>{pinnedTitle}</SupportTitle>
            {pinnedNumber && <SupportNumber>{pinnedNumber}</SupportNumber>}
          </SupportInfo>

          <SupportActions>
            <IconImgButton
              aria-label="Appeler"
              onClick={() => {
                if (!pinnedNumber) return;
                const cleanNumber = pinnedNumber.replace(/[^\d+]/g, "");
                window.location.href = `tel:${cleanNumber}`;
              }}
            >
              <IconImg src={btnTel} alt="Appeler" />
            </IconImgButton>

            <IconImgButton
              aria-label="Envoyer un SMS"
              onClick={() => {
                if (!pinnedNumber) return;
                const cleanNumber = pinnedNumber.replace(/[^\d+]/g, "");
                window.location.href = `sms:${cleanNumber}`;
              }}
            >
              <IconImg src={btnSMS} alt="SMS" />
            </IconImgButton>
          </SupportActions>
        </SupportCard>
      </SectionBlock>

      {renderListSection(
        t("mentalplus_help_num_proches"),
        "proches",
        true,
        openProchesEditor
      )}
      {renderListSection(
        t("mentalplus_help_num_urgences"),
        "urgences",
        true,
        openUrgencesEditor
      )}
      {renderListSection(
        t("mentalplus_help_num_utiles"),
        "utiles",
        true,
        openUtilesEditor
      )}

      {isPinnedPickerOpen && (
        <BottomSheet>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "2vh",
            }}
          >
            {t("mentalplus_help_num_epingle_choix")}
          </h3>

          <SheetCloseButton onClick={() => setIsPinnedPickerOpen(false)}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <ProchesListBlock>
            {emergencyData.urgences.length +
              emergencyData.proches.length +
              emergencyData.utiles.length >
            0 ? (
              <>
                {[
                  ...emergencyData.urgences.map((line, idx) => ({
                    line,
                    source: "urgences" as const,
                    index: idx,
                  })),
                  ...emergencyData.proches.map((line, idx) => ({
                    line,
                    source: "proches" as const,
                    index: idx,
                  })),
                  ...emergencyData.utiles.map((line, idx) => ({
                    line,
                    source: "utiles" as const,
                    index: idx,
                  })),
                ].map((item) => {
                  const { title, number } = splitLine(item.line);
                  const checked =
                    pinnedSource === item.source && pinnedIndex === item.index;

                  return (
                    <ProcheRow key={`${item.source}-${item.index}`}>
                      <ProcheInfo>
                        <span className="name">{title}</span>
                        {number && <span className="phone">{number}</span>}
                      </ProcheInfo>

                      <CheckboxWrapper>
                        <IonCheckbox
                          checked={checked}
                          onIonChange={() => {
                            setPinnedSource(item.source);
                            setPinnedIndex(item.index);
                          }}
                        />
                      </CheckboxWrapper>
                    </ProcheRow>
                  );
                })}
              </>
            ) : (
              <ProchesEmpty>Aucun numéro disponible.</ProchesEmpty>
            )}
          </ProchesListBlock>

          <SaveButton
            onClick={() => {
              if (!pinnedSource || pinnedIndex === null) {
                setIsPinnedPickerOpen(false);
                return;
              }

              const list =
                pinnedSource === "urgences"
                  ? emergencyData.urgences
                  : pinnedSource === "proches"
                  ? emergencyData.proches
                  : emergencyData.utiles;

              if (!list[pinnedIndex]) {
                setIsPinnedPickerOpen(false);
                return;
              }

              updateStorage({ ...emergencyData, pinned: list[pinnedIndex] });
              setIsPinnedPickerOpen(false);
            }}
          >
            {t("mentalplus_help_num_valider")}
          </SaveButton>
        </BottomSheet>
      )}

      {isEditUrgencesOpen && (
        <BottomSheet>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "2vh",
            }}
          >
            {t("mentalplus_help_num_gerer")}
          </h3>

          <SheetCloseButton onClick={() => setIsEditUrgencesOpen(false)}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <ProchesListBlock>
            {emergencyData.urgences.length > 0 ? (
              sortAlphabetically(emergencyData.urgences).map((line, idx) => {
                const { title, number } = splitLine(line);
                const checked = selectedUrgences.includes(idx);

                return (
                  <ProcheRow key={idx}>
                    <ProcheInfo>
                      <span className="name">{title}</span>
                      {number && <span className="phone">{number}</span>}
                    </ProcheInfo>
                    <ToggleWrapper>
                      <IonToggle
                        checked={checked}
                        onIonChange={() => toggleUrgenceSelection(idx)}
                      />
                    </ToggleWrapper>
                  </ProcheRow>
                );
              })
            ) : (
              <ProchesEmpty>Aucun numéro d’urgence défini.</ProchesEmpty>
            )}
          </ProchesListBlock>

          <SaveButton onClick={saveUrgencesSelection}>
            {t("mentalplus_help_num_enregistrer")}
          </SaveButton>
        </BottomSheet>
      )}

      {isEditProchesOpen && (
        <BottomSheet>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "2vh",
            }}
          >
            {t("mentalplus_help_num_gerer_proches")}
          </h3>

          <SheetCloseButton onClick={closeProchesEditor}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <ProchesListBlock>
            {emergencyData.proches.length > 0 ? (
              sortAlphabetically(emergencyData.proches).map((line) => {
                const { title, number } = splitLine(line);

                return (
                  <SupportCard key={line}>
                    <SupportInfo>
                      <SupportTitle>{title}</SupportTitle>
                      {number && <SupportNumber>{number}</SupportNumber>}
                    </SupportInfo>

                    <SupportActions>
                      <IconImgButton
                        aria-label="Supprimer ce proche"
                        onClick={() => removeProche(line)}
                      >
                        <IconImg src={btnSuppr} alt="Supprimer" />
                      </IconImgButton>
                    </SupportActions>
                  </SupportCard>
                );
              })
            ) : (
              <ProchesEmpty>
                Aucun proche enregistré pour le moment.
              </ProchesEmpty>
            )}
          </ProchesListBlock>

          {!isAddingProche && (
            <AddProcheToggleButton
              type="button"
              onClick={() => setIsAddingProche(true)}
            >
              {t("mentalplus_help_num_proches_ajouter")}
            </AddProcheToggleButton>
          )}

          {isAddingProche && (
            <AddBlock>
              <Input
                type="text"
                placeholder={t("mentalplus_help_num_proches_nom")}
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
              <Input
                type="tel"
                placeholder={t("mentalplus_help_num_proches_nom_numero")}
                value={manualPhone}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^\d+]/g, "");
                  setManualPhone(onlyNumbers);
                }}
              />
              <AddProcheButton onClick={addManualProche}>
                {t("mentalplus_help_num_enregistrer")}
              </AddProcheButton>
            </AddBlock>
          )}
        </BottomSheet>
      )}

      {isEditUtilesOpen && (
        <BottomSheet>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "2vh",
            }}
          >
            {t("mentalplus_help_num_gerer")}
          </h3>

          <SheetCloseButton onClick={() => setIsEditUtilesOpen(false)}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <ProchesListBlock>
            {emergencyData.utiles.length > 0 ? (
              sortAlphabetically(emergencyData.utiles).map((line, idx) => {
                const { title, number } = splitLine(line);
                const checked = selectedUsefulIndexes.includes(idx);

                return (
                  <ProcheRow key={idx}>
                    <ProcheInfo>
                      <span className="name">{title}</span>
                      {number && <span className="phone">{number}</span>}
                    </ProcheInfo>
                    <ToggleWrapper>
                      <IonToggle
                        checked={checked}
                        onIonChange={() => toggleUsefulSelection(idx)}
                      />
                    </ToggleWrapper>
                  </ProcheRow>
                );
              })
            ) : (
              <ProchesEmpty>Aucun numéro utile défini.</ProchesEmpty>
            )}
          </ProchesListBlock>

          <SaveButton onClick={saveUsefulSelection}>
            {t("mentalplus_help_num_enregistrer")}
          </SaveButton>
        </BottomSheet>
      )}
    </EmergencyWrapper>
  );
}

/* -------------------------------------------------------------------------- */
/*                                SIGNES CARDS                                */
/* -------------------------------------------------------------------------- */

function SymptomCards() {
  const [cards, setCards] = useState<string[]>(() => {
    const stored = localStorage.getItem("symptomCards");
    return stored ? JSON.parse(stored) : [];
  });

  const [newSign, setNewSign] = useState("");

  const updateStorage = (newCards: string[]) => {
    setCards(newCards);
    localStorage.setItem("symptomCards", JSON.stringify(newCards));
  };

  const removeCard = (indexToRemove: number) => {
    updateStorage(cards.filter((_, i) => i !== indexToRemove));
  };

  const addCard = () => {
    if (newSign.trim()) {
      updateStorage([...cards, newSign.trim()]);
      setNewSign("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") addCard();
  };

  return (
    <SignesWrapper>
      <SignesTitle>Mes ressentis</SignesTitle>

      {/* Card 1 : saisie */}
      <SignesCard>
        <SignesCardHeader>
          <SignesPersonIcon viewBox="0 0 24 24">
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="#6b7280"/>
          </SignesPersonIcon>
          <SignesCardLabel>Décrire un signe</SignesCardLabel>
        </SignesCardHeader>
        <SignesInputRow>
          <SignesInput
            type="text"
            placeholder="J'ajoute mon signe"
            value={newSign}
            onChange={(e) => setNewSign(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SignesAddBtn onClick={addCard}>+</SignesAddBtn>
        </SignesInputRow>
      </SignesCard>

      {/* Card 2 : liste */}
      <SignesCard>
        <SignesCardHeader>
          <SignesPersonIcon viewBox="0 0 24 24">
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="#6b7280"/>
          </SignesPersonIcon>
          <div>
            <SignesCardLabel>Ma liste</SignesCardLabel>
            <SignesCardSub>Les signes auxquels je m'identifie</SignesCardSub>
          </div>
        </SignesCardHeader>
        <SignesTagsWrapper>
          {cards.map((text, idx) => (
            <SignesTag key={idx}>
              {text}
              <SignesTagRemove onClick={() => removeCard(idx)}>×</SignesTagRemove>
            </SignesTag>
          ))}
        </SignesTagsWrapper>
      </SignesCard>
    </SignesWrapper>
  );
}

/* -------------------------------------------------------------------------- */
/*                              J'ÉCRIS CARDS                                 */
/* -------------------------------------------------------------------------- */

type JEcrisCardsProps = {
  isAdding: boolean;
  setIsAdding: React.Dispatch<React.SetStateAction<boolean>>;
};

function JEcrisCards({ isAdding, setIsAdding }: JEcrisCardsProps) {
  const [notes, setNotes] = useState<string[]>(() => {
    const stored = localStorage.getItem("jecrisCards");
    return stored ? JSON.parse(stored) : [];
  });

  const [newNote, setNewNote] = useState("");
  const { t } = useTranslation();

  const updateStorage = (newCards: string[]) => {
    setNotes(newCards);
    localStorage.setItem("jecrisCards", JSON.stringify(newCards));
  };

  const removeCard = (indexToRemove: number) => {
    updateStorage(notes.filter((_, index) => index !== indexToRemove));
  };

  const addCard = () => {
    if (newNote.trim() !== "") {
      updateStorage([...notes, newNote.trim()]);
      setNewNote("");
      setIsAdding(false);
    }
  };

  return (
    <>
      <SimpleCardsColumn>
        {notes.map((text, index) => (
          <SupportCard key={index}>
            <SupportInfo>
              <SimpleTitle>{text}</SimpleTitle>
            </SupportInfo>
            <SupportActions>
              <IconImgButton aria-label="Supprimer" onClick={() => removeCard(index)}>
                <IconImg src={btnSuppr} alt="Supprimer" />
              </IconImgButton>
            </SupportActions>
          </SupportCard>
        ))}
      </SimpleCardsColumn>

      {isAdding && (
        <BottomSheet>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", marginBottom: "6vh" }}>
            {t("mentalplus_help_jecris_ajouter")}
          </h3>
          <SheetCloseButton onClick={() => setIsAdding(false)}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>
          <Input
            type="text"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <AddConfirmButton onClick={addCard}>
            {t("mentalplus_help_ajouter")}
          </AddConfirmButton>
        </BottomSheet>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                   CONTACTS & URGENCE CARDS (Numéros de soutiens tab)      */
/* -------------------------------------------------------------------------- */

function ContactsEmergencyCards() {
  const { t } = useTranslation();

  const defaultData: EmergencyData = {
    pinned: "Prévention suicide – 3114",
    urgences: [
      "Prévention suicide – 3114",
      "Urgence vitale – 112",
      "Croix-Rouge écoute – 0 800 858 858",
      "SOS Amitié – 09 72 39 40 50",
      "SOS crise – 0800 19 00 00",
    ],
    proches: [],
    utiles: [
      "Fil Santé Jeunes – 0 800 235 236",
      "Planning familial – 0 800 08 11 11",
      "Net Écoute – 3018",
    ],
  };

  const [emergencyData, setEmergencyData] = useState<EmergencyData>(() => {
    const stored = localStorage.getItem("emergencyDataV2");
    if (!stored) return defaultData;
    try {
      const parsed = JSON.parse(stored);
      return {
        pinned: parsed.pinned ?? defaultData.pinned,
        urgences: Array.isArray(parsed.urgences) ? parsed.urgences : defaultData.urgences,
        proches: Array.isArray(parsed.proches) ? parsed.proches : defaultData.proches,
        utiles: Array.isArray(parsed.utiles) ? parsed.utiles : defaultData.utiles,
      };
    } catch {
      return defaultData;
    }
  });

  const updateStorage = (newData: EmergencyData) => {
    setEmergencyData(newData);
    localStorage.setItem("emergencyDataV2", JSON.stringify(newData));
  };

  const splitLine = (line: string) => {
    const [title, number] = line.split("–");
    return { title: title?.trim() ?? "", number: number?.trim() ?? "" };
  };

  const callNumber = (number: string) => {
    const clean = number.replace(/[^\d+]/g, "");
    window.location.href = `tel:${clean}`;
  };
  const smsNumber = (number: string) => {
    const clean = number.replace(/[^\d+]/g, "");
    window.location.href = `sms:${clean}`;
  };

  /* ----------------------------- Proches ---------------------------------- */

  const [isAddingProche, setIsAddingProche] = useState(false);
  const [editingProche, setEditingProche] = useState<string | null>(null);
  const [manualName, setManualName] = useState("");
  const [manualPhone, setManualPhone] = useState("");

  const openSheet = () => document.body.classList.add("sheet-open");
  const closeSheet = () => document.body.classList.remove("sheet-open");

  const openAddProche = () => {
    setManualName("");
    setManualPhone("");
    setIsAddingProche(true);
    openSheet();
  };

  const openEditProche = (line: string) => {
    const { title, number } = splitLine(line);
    setManualName(title);
    setManualPhone(number);
    setEditingProche(line);
    openSheet();
  };

  const saveNewProche = () => {
    if (!manualName.trim() || !manualPhone.trim()) return;
    const line = `${manualName.trim()} – ${manualPhone.trim()}`;
    updateStorage({ ...emergencyData, proches: [...emergencyData.proches, line] });
    setManualName("");
    setManualPhone("");
    setIsAddingProche(false);
    closeSheet();
  };

  const saveEditedProche = () => {
    if (!manualName.trim() || !manualPhone.trim() || !editingProche) return;
    const newLine = `${manualName.trim()} – ${manualPhone.trim()}`;
    const updatedProches = emergencyData.proches.map((l) => (l === editingProche ? newLine : l));
    const newPinned = emergencyData.pinned === editingProche ? newLine : emergencyData.pinned;
    updateStorage({ ...emergencyData, proches: updatedProches, pinned: newPinned });
    setManualName("");
    setManualPhone("");
    setEditingProche(null);
    closeSheet();
  };

  const deleteProche = (line: string) => {
    const updatedProches = emergencyData.proches.filter((l) => l !== line);
    let newPinned = emergencyData.pinned;
    if (line === emergencyData.pinned) {
      newPinned =
        updatedProches[0] ??
        emergencyData.urgences[0] ??
        emergencyData.utiles[0] ??
        "";
    }
    updateStorage({ ...emergencyData, proches: updatedProches, pinned: newPinned });
    setActiveUrgenceLines((prev) => prev.filter((l) => l !== line));
    setEditingProche(null);
    closeSheet();
  };

  /* ----------------------------- Urgences actives -------------------------- */

  const [activeUrgenceLines, setActiveUrgenceLines] = useState<string[]>(() => {
    const stored = localStorage.getItem("activeUrgenceLinesV2");
    if (!stored) return ["Prévention suicide – 3114"];
    try { return JSON.parse(stored); } catch { return ["Prévention suicide – 3114"]; }
  });

  const [isEditUrgenceOpen, setIsEditUrgenceOpen] = useState(false);

  const toggleUrgenceLine = (line: string) => {
    // Un seul numéro actif à la fois
    const updated = activeUrgenceLines.includes(line) ? [] : [line];
    setActiveUrgenceLines(updated);
    localStorage.setItem("activeUrgenceLinesV2", JSON.stringify(updated));
  };

  const allAvailableLines = [
    ...emergencyData.urgences,
    ...emergencyData.proches,
  ];

  return (
    <EmergencyWrapper>
      {/* Personnes de confiance */}
      <SectionBlock>
        <SectionLabel style={{ color: "#1a1a1a" }}>
          {t("mentalplus_help_num_confiance")}
        </SectionLabel>

        <CardsColumn>
          {emergencyData.proches.map((line, idx) => {
            const { title, number } = splitLine(line);
            return (
              <NewContactCard key={idx}>
                <NewCardLeft>
                  <NewCardName>{title}</NewCardName>
                  {number && <NewCardPhone>{number}</NewCardPhone>}
                </NewCardLeft>
                <NewCardRight>
                  <NewCardEditBtn aria-label="Modifier" onClick={() => openEditProche(line)}>
                    <NewCardEditImg src={iconModify} alt="Modifier" />
                  </NewCardEditBtn>
                  <NewCardActions>
                    <NewCardActionBtn aria-label="Appeler" onClick={() => callNumber(number)}>
                      <NewCardActionImg src={btnTel} alt="Appeler" />
                    </NewCardActionBtn>
                    <NewCardActionBtn aria-label="Envoyer un SMS" onClick={() => smsNumber(number)}>
                      <NewCardActionImg src={btnSMS} alt="SMS" />
                    </NewCardActionBtn>
                  </NewCardActions>
                </NewCardRight>
              </NewContactCard>
            );
          })}

          {/* Add contact card */}
          <AddProcheCard onClick={openAddProche}>
            <NewCardBody>
              <AddProcheCategory>Catégorie</AddProcheCategory>
              <AddProcheName>Nom du contact</AddProcheName>
            </NewCardBody>
            <AddProcheBtn>+</AddProcheBtn>
          </AddProcheCard>
        </CardsColumn>
      </SectionBlock>

      {/* Numéro d'urgence */}
      <SectionBlock>
        <SectionLabel style={{ color: "#1a1a1a" }}>
          {t("mentalplus_help_num_urgence_single")}
        </SectionLabel>

        <CardsColumn>
          {activeUrgenceLines.map((line, idx) => {
            const { title, number } = splitLine(line);
            return (
              <NewContactCard key={idx}>
                <NewCardLeft>
                  <NewCardName>{title}</NewCardName>
                  {number && <NewCardPhone>{number}</NewCardPhone>}
                </NewCardLeft>
                <NewCardRight>
                  <NewCardEditBtn aria-label="Modifier" onClick={() => { setIsEditUrgenceOpen(true); openSheet(); }}>
                    <NewCardEditImg src={iconModify} alt="Modifier" />
                  </NewCardEditBtn>
                  <NewCardActions>
                    <NewCardActionBtn aria-label="Appeler" onClick={() => callNumber(number)}>
                      <NewCardActionImg src={btnTel} alt="Appeler" />
                    </NewCardActionBtn>
                    <NewCardActionBtn aria-label="Envoyer un SMS" onClick={() => smsNumber(number)}>
                      <NewCardActionImg src={btnSMS} alt="SMS" />
                    </NewCardActionBtn>
                  </NewCardActions>
                </NewCardRight>
              </NewContactCard>
            );
          })}
        </CardsColumn>
      </SectionBlock>

      {/* Bottom sheet: ajouter un proche */}
      {isAddingProche && (
        <>
          <BottomSheet>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", marginBottom: "6vh" }}>
            {t("mentalplus_help_num_proches_ajouter")}
          </h3>
          <SheetCloseButton onClick={() => { setIsAddingProche(false); closeSheet(); }}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>
          <Input
            type="text"
            placeholder={t("mentalplus_help_num_proches_nom")}
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
          />
          <Input
            type="tel"
            placeholder={t("mentalplus_help_num_proches_nom_numero")}
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            style={{ marginTop: 12 }}
          />
          <AddConfirmButton onClick={saveNewProche}>
            {t("mentalplus_help_ajouter")}
          </AddConfirmButton>
        </BottomSheet>
        </>
      )}

      {/* Bottom sheet: éditer un proche */}
      {editingProche && (
        <>
          <BottomSheet>
          <UrgenceModalTitle>Modifier</UrgenceModalTitle>
          <SheetCloseButton onClick={() => { setEditingProche(null); closeSheet(); }}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <IconInputWrapper>
            <InputIconSvg viewBox="0 0 24 24" fill="none">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="#aaa"/>
            </InputIconSvg>
            <IconInputField
              type="text"
              placeholder="Nom"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
          </IconInputWrapper>

          <IconInputWrapper>
            <InputIconSvg viewBox="0 0 24 24" fill="none">
              <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.5 0 1 .4 1 1V20c0 .5-.5 1-1 1C9.6 21 3 14.4 3 6c0-.5.4-1 1-1h3.5c.5 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1l-2.3-1.8z" fill="#aaa"/>
            </InputIconSvg>
            <IconInputField
              type="tel"
              placeholder="Numéro de téléphone"
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
            />
          </IconInputWrapper>

          <ValiderButton onClick={saveEditedProche}>Valider</ValiderButton>
          <DeleteContactButton onClick={() => deleteProche(editingProche)}>
            Supprimer
          </DeleteContactButton>
        </BottomSheet>
        </>
      )}

      {/* Modal: sélectionner les numéros d'urgence actifs */}
      {isEditUrgenceOpen && (
        <>
          <UrgenceListSheet>
          <UrgenceModalTitle>Modifier</UrgenceModalTitle>
          <SheetCloseButton onClick={() => { setIsEditUrgenceOpen(false); closeSheet(); }}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>
          <UrgenceModalSubtitle>
            Sélectionne la plateforme à contacter en cas d'urgence.
          </UrgenceModalSubtitle>
          <UrgenceToggleList>
            {allAvailableLines.map((line, idx) => {
              const { title, number } = splitLine(line);
              const isActive = activeUrgenceLines.includes(line);
              return (
                <UrgenceToggleRow key={idx}>
                  <UrgenceToggleInfo>
                    <span className="name">{title}</span>
                    {number && <span className="phone">{number}</span>}
                  </UrgenceToggleInfo>
                  <IonToggle
                    checked={isActive}
                    onIonChange={() => toggleUrgenceLine(line)}
                  />
                </UrgenceToggleRow>
              );
            })}
          </UrgenceToggleList>
        </UrgenceListSheet>
        </>
      )}
    </EmergencyWrapper>
  );
}

/* -------------------------------------------------------------------------- */
/*                               ACTIVITÉS CARDS                              */
/* -------------------------------------------------------------------------- */

type ActivityCardsProps = {
  isAdding: boolean;
  setIsAdding: React.Dispatch<React.SetStateAction<boolean>>;
};

function ActivityCards({ isAdding, setIsAdding }: ActivityCardsProps) {
  const [activities, setActivities] = useState<string[]>(() => {
    const stored = localStorage.getItem("activityCards");
    return stored ? JSON.parse(stored) : [];
  });

  const [newActivity, setNewActivity] = useState("");
  const { t } = useTranslation();

  const updateStorage = (newCards: string[]) => {
    setActivities(newCards);
    localStorage.setItem("activityCards", JSON.stringify(newCards));
  };

  const removeCard = (indexToRemove: number) => {
    const newCards = activities.filter((_, index) => index !== indexToRemove);
    updateStorage(newCards);
  };

  const addCard = () => {
    if (newActivity.trim() !== "") {
      updateStorage([...activities, newActivity.trim()]);
      setNewActivity("");
      setIsAdding(false);
    }
  };

  return (
    <>
      <SimpleCardsColumn>
        {activities.map((text, index) => (
          <SupportCard key={index}>
            <SupportInfo>
              <SimpleTitle>{text}</SimpleTitle>
            </SupportInfo>

            <SupportActions>
              <IconImgButton
                aria-label="Supprimer"
                onClick={() => removeCard(index)}
              >
                <IconImg src={btnSuppr} alt="Supprimer" />
              </IconImgButton>
            </SupportActions>
          </SupportCard>
        ))}
      </SimpleCardsColumn>

      {isAdding && (
        <BottomSheet>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "6vh",
            }}
          >
            {t("mentalplus_help_solutions_ajouter")}
          </h3>

          <SheetCloseButton onClick={() => setIsAdding(false)}>
            <SheetCloseImg src={btnSuppr} alt="Fermer" />
          </SheetCloseButton>

          <Input
            type="text"
            value={newActivity}
            onChange={(e) => setNewActivity(e.target.value)}
          />

          <AddConfirmButton onClick={addCard}>
            {t("mentalplus_help_ajouter")}
          </AddConfirmButton>
        </BottomSheet>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                              COMPOSANT PRINCIPAL                           */
/* -------------------------------------------------------------------------- */

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<MainTab>(() => {
    const stored = localStorage.getItem("activeTab");
    return (stored as MainTab) || "urgence";
  });

  const [isAddingActivites, setIsAddingActivites] = useState(false);
  const [isAddingJEcris, setIsAddingJEcris] = useState(false);
  const { t } = useTranslation();
  const { setResonanceLayout } = useResonanceLayout();

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "mentalplus_tab_help",
      welcomePage: false,
      headerBackground: "white",
    });
  });

  useIonViewWillLeave(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      headerBackground: "transparent",
    });
  });

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        <TabsWrapper>
          <div
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 1000,
              padding: "8px 0",
            }}
          >
            <StyledSegment
              value={activeTab}
              onIonChange={(e) => {
                const tab = (e.detail.value as MainTab) || "urgence";
                setActiveTab(tab);
                localStorage.setItem("activeTab", tab);
              }}
            >
              <IonSegmentButton value="urgence">
                <IonLabel>{t("mentalplus_help_num_soutiens")}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="signes">
                <IonLabel>{t("mentalplus_help_signes")}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="activites">
                <IonLabel>{t("mentalplus_help_solutions")}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="jecris">
                <IonLabel>{t("mentalplus_help_jecris")}</IonLabel>
              </IonSegmentButton>
            </StyledSegment>
          </div>

          <HeaderSlot>
            {activeTab === "activites" && (
              <SectionHeader>
                <QuestionHeading>
                  {t("mentalplus_help_solutions_question")}
                </QuestionHeading>
                <AddButton onClick={() => setIsAddingActivites(true)}>
                  +
                </AddButton>
              </SectionHeader>
            )}

            {activeTab === "jecris" && (
              <SectionHeader>
                <QuestionHeading>
                  {t("mentalplus_help_jecris_question")}
                </QuestionHeading>
                <AddButton onClick={() => setIsAddingJEcris(true)}>+</AddButton>
              </SectionHeader>
            )}
          </HeaderSlot>
        </TabsWrapper>

        {activeTab === "signes" && (
          <SignesContent>
            <SymptomCards />
          </SignesContent>
        )}

        {activeTab === "activites" && (
          <ContentSimple>
            <ActivityCards
              isAdding={isAddingActivites}
              setIsAdding={setIsAddingActivites}
            />
          </ContentSimple>
        )}

        {activeTab === "urgence" && (
          <Content>
            <ContactsEmergencyCards />
          </Content>
        )}

        {activeTab === "jecris" && (
          <ContentSimple>
            <JEcrisCards
              isAdding={isAddingJEcris}
              setIsAdding={setIsAddingJEcris}
            />
          </ContentSimple>
        )}
      </PageWrapper>
    </ResonanceLayout>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const HeaderSlot = styled.div`
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    background: #fff;
    z-index: -1;
    pointer-events: none;
  }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-width: 600px;
  margin: 0 auto;
  background: white;
  font-family: sans-serif;
`;

const TabsWrapper = styled.div`
  display: flex;
  position: fixed;
  flex-direction: column;
  align-self: center;
  background: white;
  z-index: 10000;
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 1rem 120px;
  padding-top: 70px;
  font-size: 16px;

  @media (max-width: 360px) {
    padding-top: 50px;
  }
`;

const ContentSimple = styled.div`
  flex: 1;
  padding: 24px 1rem 120px;
  padding-top: 130px;
  font-size: 16px;

  @media (max-width: 360px) {
    padding-top: 110px;
  }
`;

const SimpleCardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
  margin-top: 10px;
  flex-wrap: nowrap;
`;

const QuestionHeading = styled.h2`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  word-break: break-word;
  padding-left: 12px;
  padding-right: 8px;

  @media (max-width: 360px) {
    font-size: 13px;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto;
  align-self: flex-start;
  background-color: #4285f4;
  color: white;
  font-size: 22px;
  border: none;
  border-radius: 12px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 8px;

  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
`;


const BottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 55vh;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 4vw 5vw calc(0vw + env(safe-area-inset-bottom, 0px)) 5vw;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
`;

const SheetCloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  line-height: 0;
  position: absolute;
  top: 0px;
  right: 10px;
`;

const SheetCloseImg = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border-radius: 12px;
  border: 1px solid #ccc;
`;

const AddConfirmButton = styled.button`
  width: 100%;
  background-color: #4285f4;
  color: white;
  padding: 12px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 16px;
`;

const StyledSegment = styled(IonSegment)`
  width: 100%;

  ion-segment-button {
    flex: 1;
    min-width: 0;
    padding-inline: 2px;
  }

  ion-segment-button ion-label {
    display: block;
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.1;
    font-size: 11px;
    letter-spacing: 0;
  }

  @media (max-width: 360px) {
    ion-segment-button ion-label {
      font-size: 10px;
    }
  }
`;

const EmergencyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
`;

const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px 6px 4px;
`;

const SectionLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #4285f4;
  margin: 0;

  @media (max-width: 360px) {
    font-size: 15px;
  }
`;

const SectionIconButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const HeaderIconImg = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  user-select: none;
`;

const CardsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SupportCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  padding: 0px 16px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 16px;
    pointer-events: none;
  }
`;

const SupportInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SupportTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const SupportNumber = styled.div`
  font-size: 13px;
  opacity: 1.7;
  font-weight: 400;
`;

const SupportActions = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  z-index: 5;

  & > button:first-child {
    margin-right: -6px;
  }
`;

const IconImgButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 60px;
  height: 60px;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;

  &:active {
    transform: scale(0.97);
  }
`;

const IconImg = styled.img`
  width: 76px;
  height: 76px;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
`;

const ProchesListBlock = styled.div`
  background: #f5f7ff;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 10px;
  max-height: 51vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProcheRow = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  padding: 13px 16px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 16px;
    pointer-events: none;
  }
`;

const CheckboxWrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  ion-checkbox {
    --size: 22px;
  }
`;

const ProcheInfo = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    font-size: 14px;
    font-weight: 600;
  }

  .phone {
    font-size: 13px;
    opacity: 1.7;
  }
`;

const ProchesEmpty = styled.div`
  font-size: 13px;
  opacity: 0.7;
  padding: 6px 2px;
`;

const SaveButton = styled(AddConfirmButton)`
  margin-top: auto;
  margin-bottom: 70px;
  border-radius: 20px;
`;

const AddProcheButton = styled(AddConfirmButton)`
  margin-top: 12px;
  border-radius: 20px;
`;

const AddProcheToggleButton = styled(AddConfirmButton)`
  margin-top: auto;
  margin-bottom: 70px;
  border-radius: 20px;
`;

const AddBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  margin-bottom: 70px;
`;

const ToggleWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const SimpleTitle = styled.div`
  font-size: 15px;
  font-weight: 400;
`;

/* ---- New contact card styles (Signes tab) ---- */

const NewContactCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  padding: 10px 6px 6px 16px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 64px;
`;

const NewCardLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 4px;
  gap: 6px;
`;

const NewCardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
`;

const NewCardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const NewCardName = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const NewCardPhone = styled.div`
  font-size: 13px;
  color: #555;
`;

const NewCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NewCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const NewCardEditBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
`;

const NewCardEditImg = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
`;

const NewCardActionBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:active {
    transform: scale(0.97);
  }
`;

const NewCardActionImg = styled.img`
  width: 42px;
  height: 42px;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
`;

const AddProcheCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const AddProcheCategory = styled.div`
  font-size: 14px;
  color: #aaa;
`;

const AddProcheName = styled.div`
  font-size: 14px;
  color: #aaa;
`;

const AddProcheBtn = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: transparent;
  border: 2px solid #aaa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #aaa;
  flex-shrink: 0;
`;

const SosButtonWrapper = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 60px;
  height: 60px;

  &:active {
    transform: scale(0.97);
  }
`;

const SosLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #f5a623;
  margin-top: -8px;
`;

const UrgenceToggleList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-bottom: 24px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const UrgenceToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const UrgenceToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .name {
    font-size: 15px;
    font-weight: 500;
    color: #1a1a1a;
  }

  .phone {
    font-size: 13px;
    color: #888;
  }
`;

const UrgenceListSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  max-height: calc(100vh - 160px);
  height: calc(100vh - 160px);
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 4vw 5vw calc(0vw + env(safe-area-inset-bottom, 0px)) 5vw;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const UrgenceModalTitle = styled.h3`
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
`;

const IconInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f4f6fb;
  border-radius: 12px;
  padding: 0 14px;
  margin-bottom: 14px;
  height: 52px;
`;

const InputIconSvg = styled.svg`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-right: 10px;
`;

const IconInputField = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 15px;
  color: #1a1a1a;
  outline: none;

  &::placeholder {
    color: #aaa;
  }
`;

const ValiderButton = styled.button`
  width: 100%;
  background-color: #f5c842;
  color: #1a1a1a;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  margin-top: 8px;

  &:active {
    opacity: 0.85;
  }
`;

const UrgenceModalSubtitle = styled.p`
  font-size: 14px;
  color: #4285f4;
  margin: 0 0 16px 0;
  line-height: 1.4;
`;

/* ---- Signes tab styles ---- */

const SignesContent = styled.div`
  flex: 1;
  padding: 24px 1rem 120px;
  padding-top: 90px;

  @media (max-width: 360px) {
    padding-top: 70px;
  }
`;

const SignesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SignesTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
`;

const SignesCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SignesCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const SignesPersonIcon = styled.svg`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const SignesCardLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
`;

const SignesCardSub = styled.div`
  font-size: 13px;
  color: #9ca3af;
  margin-top: 2px;
`;

const SignesInputRow = styled.div`
  display: flex;
  align-items: center;
  background: #f4f6fb;
  border-radius: 10px;
  padding: 0 12px;
  height: 44px;
`;

const SignesInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #1a1a1a;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`;

const SignesAddBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:active {
    color: #6366f1;
  }
`;

const SignesTagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SignesTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #6366f1;
  color: white;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 20px;
`;

const SignesTagRemove = styled.button`
  border: none;
  background: transparent;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
`;

const DeleteContactButton = styled.button`
  width: 100%;
  background-color: #ff4444;
  color: white;
  padding: 12px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 12px;
`;